/**
 * ScreenHeader — Reusable header for stack screens.
 * Back button (teal) + title + optional right action.
 */

import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import Ionicons from './Ionicons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface ScreenHeaderProps {
  title: string;
  headerRight?: React.ReactNode;
  onBack?: () => void;
}

export function ScreenHeader({ title, headerRight, onBack }: ScreenHeaderProps) {
  const navigation = useNavigation();
  const { t } = useTranslation();
  const { top } = useSafeAreaInsets();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const canGoBack = navigation.canGoBack();

  return (
    <View style={[styles.container, { paddingTop: top, backgroundColor: theme.card, borderBottomColor: theme.border }]}>
      <View style={styles.row}>
        <View style={styles.side}>
          {canGoBack && (
            <TouchableOpacity
              testID="header-back"
              accessibilityLabel={t('common.back')}
              accessibilityRole="button"
              onPress={onBack ?? (() => navigation.goBack())}
              style={styles.touchTarget}
            >
              <Ionicons name="chevron-back" size={28 * fontScale} color={colors.primary} />
            </TouchableOpacity>
          )}
        </View>

        <Text
          allowFontScaling={false}
          numberOfLines={1}
          ellipsizeMode="tail"
          style={[styles.title, { fontSize: 17 * fontScale, color: theme.text, ...ffs }]}
        >
          {title}
        </Text>

        <View style={styles.side}>
          {headerRight}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  row: {
    height: 44,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  side: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  touchTarget: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    flex: 1,
    textAlign: 'center',
    fontWeight: '600',
  },
});
